import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/ServicePage.css';
import { setPageMeta } from '../utils/seo';

const TrainingSolutions = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    setPageMeta(
      'Specialized Training Solutions | HRCONSOL',
      'Tailored leadership, technical, compliance, and soft skills training with blended delivery and measurable outcomes for teams.'
    );
  }, []);

  return (
    <div className="service-page">
      <div className="service-hero">
        <div className="container">
          <h1>Specialized Training Solutions – Building Future-Ready Teams</h1>
          <p className="subtitle">HRCONSOL offers tailored training programs for workforce development, leadership excellence, compliance, and future-ready skills that drive organizational success.</p>
        </div>
      </div>

      <div className="container">
        <div className="service-content">
          <div className="service-section">
            <p>In a rapidly evolving business landscape, continuous learning and development are essential for staying competitive. HRCONSOL's <strong>Specialized Training Solutions</strong> equip your teams with the skills, knowledge, and mindset needed to excel in their roles and adapt to future challenges.</p>
            <p>From technical training to soft skills development, we design and deliver impactful learning experiences.</p>
          </div>

          <div className="service-section">
            <h2>Scope of Services</h2>
            <p>Our Training Solutions Include:</p>
            <div className="process-steps">
              <div className="step">
                <h3><i className="fas fa-users"></i> Leadership Development</h3>
                <p>Training for executives, managers, and emerging leaders.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-laptop-code"></i> Technical Skills Training</h3>
                <p>Software, technology, tools, and industry-specific technical competencies.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-comments"></i> Soft Skills Development</h3>
                <p>Communication, teamwork, problem-solving, and emotional intelligence.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-shopping-cart"></i> Sales & Customer Service</h3>
                <p>Sales techniques, customer relationship management, and service excellence.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-balance-scale"></i> Compliance & Legal Training</h3>
                <p>Labor laws, workplace safety, anti-harassment, and regulatory compliance.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-project-diagram"></i> Project Management</h3>
                <p>Agile, Scrum, PMP, and project delivery methodologies.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-user-plus"></i> Onboarding & Orientation</h3>
                <p>New employee training programs for smooth integration.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-graduation-cap"></i> Custom Training Programs</h3>
                <p>Tailored programs designed for your specific organizational needs.</p>
              </div>
            </div>
          </div>

          <div className="service-section">
            <h2>Training Delivery Modes</h2>
            <div className="process-steps">
              <div className="step">
                <h3><i className="fas fa-chalkboard-teacher"></i> In-Person Workshops</h3>
                <p>Interactive classroom training at your office or our training center.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-video"></i> Virtual Training</h3>
                <p>Live online sessions with interactive tools and engagement.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-laptop"></i> E-Learning Modules</h3>
                <p>Self-paced online courses accessible anytime, anywhere.</p>
              </div>
              <div className="step">
                <h3><i className="fas fa-users-cog"></i> Blended Learning</h3>
                <p>Combination of in-person, virtual, and e-learning methods.</p>
              </div>
            </div>
          </div>

          <div className="service-section">
            <h2>Training Categories</h2>
            <ul className="benefits-list">
              <li><i className="fas fa-user-tie"></i> Leadership & Management Training</li>
              <li><i className="fas fa-handshake"></i> Communication & Interpersonal Skills</li>
              <li><i className="fas fa-chart-line"></i> Sales & Marketing Excellence</li>
              <li><i className="fas fa-phone"></i> Customer Service Training</li>
              <li><i className="fas fa-brain"></i> Critical Thinking & Problem Solving</li>
              <li><i className="fas fa-clock"></i> Time Management & Productivity</li>
              <li><i className="fas fa-shield-alt"></i> Workplace Safety & Compliance</li>
              <li><i className="fas fa-balance-scale"></i> Diversity, Equity & Inclusion</li>
              <li><i className="fas fa-laptop-code"></i> Digital Skills & Technology</li>
              <li><i className="fas fa-money-bill-wave"></i> Financial Literacy & Management</li>
              <li><i className="fas fa-users"></i> Team Building & Collaboration</li>
              <li><i className="fas fa-lightbulb"></i> Innovation & Creative Thinking</li>
            </ul>
          </div>

          <div className="service-section">
            <h2>Why Choose HRCONSOL Training Solutions?</h2>
            <p className="track-record-intro"><strong>Proven Training Excellence:</strong></p>
            <ul className="benefits-list">
              <li>
                <span className="benefit-heading">25,000+ Professionals Trained</span>
                <span className="benefit-subheading">across various industries and levels</span>
              </li>
              <li>
                <span className="benefit-heading">500+ Training Programs Delivered</span>
                <span className="benefit-subheading">custom and standardized training solutions</span>
              </li>
              <li>
                <span className="benefit-heading">4.8/5 Training Satisfaction Rating</span>
                <span className="benefit-subheading">based on participant feedback</span>
              </li>
              <li>
                <span className="benefit-heading">Expert Trainers</span>
                <span className="benefit-subheading">certified professionals with real-world experience</span>
              </li>
              <li>
                <span className="benefit-heading">Practical & Interactive</span>
                <span className="benefit-subheading">hands-on exercises, case studies, and role-plays</span>
              </li>
              <li>
                <span className="benefit-heading">Post-Training Support</span>
                <span className="benefit-subheading">follow-up sessions, materials, and coaching</span>
              </li>
            </ul>
          </div>

          <div className="service-section">
            <h2>Our Training Process</h2>
            <div className="process-steps">
              <div className="step">
                <h3>Step 1: Training Needs Assessment</h3>
                <p>Identifying skill gaps and training requirements.</p>
              </div>
              <div className="step">
                <h3>Step 2: Program Design</h3>
                <p>Creating customized training content and curriculum.</p>
              </div>
              <div className="step">
                <h3>Step 3: Delivery</h3>
                <p>Conducting engaging, interactive training sessions.</p>
              </div>
              <div className="step">
                <h3>Step 4: Assessment</h3>
                <p>Evaluating learning through tests, projects, and feedback.</p>
              </div>
              <div className="step">
                <h3>Step 5: Follow-Up</h3>
                <p>Reinforcement sessions, coaching, and continuous support.</p>
              </div>
            </div>
          </div>

          <div className="service-section">
            <h2>Packages Details</h2>
            
            <div className="process-steps">
              <div className="step package-card">
                <h3>1. WORKSHOP</h3>
                <p>Single Training Session (1-Day)</p>
                <ul className="package-list">
                  <li>Fee: PKR 150,000 - 250,000</li>
                  <li>1-day interactive workshop</li>
                  <li>Training materials & handouts</li>
                  <li>Certificate of participation</li>
                  <li>Post-training Q&A support (1 week)</li>
                </ul>
                <p className="package-note">Perfect for one-time skill development sessions.</p>
                <button onClick={() => navigate('/#contact')} className="package-button">Choose Plan</button>
              </div>

              <div className="step package-card">
                <h3>2. PROGRAM</h3>
                <p>Multi-Session Training (3-5 Days)</p>
                <ul className="package-list">
                  <li>Fee: PKR 300,000 - 500,000</li>
                  <li>Comprehensive training program</li>
                  <li>Pre & post-training assessments</li>
                  <li>Digital resources & e-learning access</li>
                  <li>Certificate of completion</li>
                  <li>1-month post-training support</li>
                </ul>
                <p className="package-note">Ideal for in-depth skill development programs.</p>
                <button onClick={() => navigate('/#contact')} className="package-button">Choose Plan</button>
              </div>

              <div className="step package-card">
                <h3>3. ANNUAL PARTNERSHIP</h3>
                <p>Ongoing Training Support (12 Months)</p>
                <ul className="package-list">
                  <li>Fee: PKR 800,000 - 1,000,000/year</li>
                  <li>Unlimited training sessions</li>
                  <li>Custom program development</li>
                  <li>Leadership coaching</li>
                  <li>E-learning platform access</li>
                  <li>Quarterly training needs assessment</li>
                  <li>Dedicated training coordinator</li>
                </ul>
                <button onClick={() => navigate('/#contact')} className="package-button">Choose Plan</button>
              </div>
            </div>
          </div>

          <div className="service-cta">
            <h2>Ready to Upskill Your Team?</h2>
            <p>Let HRCONSOL design and deliver training that transforms your workforce.</p>
            <button onClick={() => navigate('/#contact')} className="cta-button">Contact Us</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainingSolutions;
